import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useState } from "react";
import { DRIVE_MIME_PRESETS, type RuleKind } from "./kindConfig";

export function WatchRuleKindForm({ kind, config, onChange }: { kind: RuleKind; config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v });

  switch (kind) {
    case "gmail_query":
      return (
        <>
          <Field label="Gmail query" hint={<a href="https://support.google.com/mail/answer/7190" target="_blank" rel="noreferrer" className="text-primary underline">Gmail search syntax</a>}>
            <Input value={config.query ?? ""} onChange={(e) => set("query", e.target.value)} placeholder="from:(@gsam.com) newer_than:7d" />
          </Field>
          <Field label="Max results per run">
            <Input type="number" min={1} max={200} value={config.max_results_per_run ?? 50} onChange={(e) => set("max_results_per_run", Number(e.target.value))} />
          </Field>
          <ToggleRow label="Include attachments" checked={!!config.include_attachments} onChange={(v) => set("include_attachments", v)} />
        </>
      );
    case "gmail_label":
      return (
        <>
          <Field label="Label name"><Input value={config.label ?? ""} onChange={(e) => set("label", e.target.value)} placeholder="Research" /></Field>
          <Field label="Max results per run"><Input type="number" value={config.max_results_per_run ?? 50} onChange={(e) => set("max_results_per_run", Number(e.target.value))} /></Field>
        </>
      );
    case "otter_keyword":
    case "zoom_meeting_keyword":
      return (
        <>
          <Field label="Keywords"><TagInput value={config.keywords ?? []} onChange={(v) => set("keywords", v)} placeholder="Add keyword & press Enter" /></Field>
          <Field label="Lookback days"><Input type="number" value={config.lookback_days ?? 7} onChange={(e) => set("lookback_days", Number(e.target.value))} /></Field>
        </>
      );
    case "zoom_chat_channel":
      return (
        <>
          <Field label="Channel name"><Input value={config.channel_name ?? ""} onChange={(e) => set("channel_name", e.target.value)} /></Field>
          <Field label="Lookback hours"><Input type="number" value={config.lookback_hours ?? 24} onChange={(e) => set("lookback_hours", Number(e.target.value))} /></Field>
        </>
      );
    case "zoom_ai_notes":
    case "zoom_whiteboard":
      return <Field label="Lookback days"><Input type="number" value={config.lookback_days ?? 7} onChange={(e) => set("lookback_days", Number(e.target.value))} /></Field>;
    case "drive_folder":
      return (
        <>
          <Field label="Folder ID" hint={<span className="text-xs text-muted-foreground">From URL: drive.google.com/drive/folders/<b>&lt;ID&gt;</b></span>}>
            <Input value={config.folder_id ?? ""} onChange={(e) => set("folder_id", e.target.value)} />
          </Field>
          <Field label="MIME types">
            <div className="flex flex-wrap gap-2">
              {DRIVE_MIME_PRESETS.map((m) => {
                const active = (config.mime_types ?? []).includes(m.value);
                return (
                  <button key={m.value} type="button" onClick={() => {
                    const cur: string[] = config.mime_types ?? [];
                    set("mime_types", active ? cur.filter((x) => x !== m.value) : [...cur, m.value]);
                  }} className={`px-2 py-1 text-xs rounded border ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background"}`}>{m.label}</button>
                );
              })}
            </div>
          </Field>
          <Field label="Lookback days"><Input type="number" value={config.lookback_days ?? 7} onChange={(e) => set("lookback_days", Number(e.target.value))} /></Field>
        </>
      );
    case "calendar_keyword":
      return (
        <>
          <Field label="Keywords"><TagInput value={config.keywords ?? []} onChange={(v) => set("keywords", v)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lookback days"><Input type="number" value={config.lookback_days ?? 1} onChange={(e) => set("lookback_days", Number(e.target.value))} /></Field>
            <Field label="Lookahead days"><Input type="number" value={config.lookahead_days ?? 7} onChange={(e) => set("lookahead_days", Number(e.target.value))} /></Field>
          </div>
        </>
      );
    case "asana_project":
      return (
        <>
          <Field label="Project GID"><Input value={config.project_gid ?? ""} onChange={(e) => set("project_gid", e.target.value)} /></Field>
          <ToggleRow label="Include completed tasks" checked={!!config.include_completed} onChange={(v) => set("include_completed", v)} />
          <Field label="Lookback days"><Input type="number" value={config.lookback_days ?? 7} onChange={(e) => set("lookback_days", Number(e.target.value))} /></Field>
        </>
      );
    case "gsheet_range":
      return (
        <>
          <Field label="Spreadsheet ID"><Input value={config.spreadsheet_id ?? ""} onChange={(e) => set("spreadsheet_id", e.target.value)} /></Field>
          <Field label="Sheet name"><Input value={config.sheet_name ?? ""} onChange={(e) => set("sheet_name", e.target.value)} placeholder="Universe" /></Field>
          <Field label="Range (A1 notation)"><Input value={config.range ?? ""} onChange={(e) => set("range", e.target.value)} placeholder="A1:Z500" /></Field>
        </>
      );
    case "fred_series":
      return <Field label="FRED series IDs"><TagInput value={config.series_ids ?? []} onChange={(v) => set("series_ids", v)} placeholder="DGS10, VIXCLS, …" /></Field>;
  }
}

function Field({ label, hint, children }: { label: string; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
      {hint && <div className="mt-1 text-xs">{hint}</div>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 mt-3 text-sm">
      <Switch checked={checked} onCheckedChange={onChange} /> {label}
    </label>
  );
}

function TagInput({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [input, setInput] = useState("");
  const add = () => {
    const t = input.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setInput("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {value.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1">
            {v}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== v))}><X className="h-3 w-3" /></button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button type="button" variant="outline" onClick={add}>Add</Button>
      </div>
    </div>
  );
}
